/**
 * 本地表格状态管理 Hook
 * @description 统一管理表格的排序和分页状态，支持通用排序和指标排序两种模式
 */
import { useState } from 'react';
import { PagingQuery } from 'tea-component';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { multiRatioMetricSort } from '@intelligence/v3/utils/metric';
import { SortType, SortMode } from '@home/enums/common';
import { DEFAULT_SENSORTOWER_DATA_SOURCE } from '../constants';

// ==================== 类型定义 ====================

/**
 * 通用排序状态
 */
export interface ISortState {
  /** 排序字段 */
  field: string;
  /** 排序方式 */
  order: 'asc' | 'desc';
}

/**
 * 分页信息
 */
export interface IPaginationInfo {
  /** 当前页码 */
  pageIndex: number;
  /** 每页条数 */
  pageSize: number;
  /** 总条数 */
  total: number;
  /** 总页数 */
  totalPage: number;
  /** 是否有上一页 */
  hasPrev: boolean;
  /** 是否有下一页 */
  hasNext: boolean;
}

/**
 * 重置状态返回值
 */
export interface IResetStateReturn<T> {
  /** 重置后的排序状态（指标排序模式） */
  sorts: IRatioMetricSortState<T>[];
  /** 重置后的排序状态（通用排序模式） */
  sortState: ISortState | null;
  /** 重置后的分页状态 */
  pagingQuery: PagingQuery;
}

/**
 * Hook 配置选项
 */
interface IUseLocalTableStateOptions<T> {
  /** 默认分页大小 */
  defaultPageSize?: number;
  /** 分页大小选项 */
  pageSizeOptions?: number[];
  /** 默认排序状态（指标排序模式） */
  defaultSorts?: IRatioMetricSortState<T>[];
  /** 默认排序状态（通用排序模式） */
  defaultSortState?: ISortState;
  /** 排序模式：metric-指标排序（默认），generic-通用排序 */
  sortMode?: 'metric' | 'generic';
  /** 自定义排序比较函数（通用排序模式下使用） */
  customComparator?: (a: T, b: T, field: string, order: 'asc' | 'desc') => number;
}

/**
 * Hook 返回值
 */
interface IUseLocalTableStateReturn<T> {
  // ==================== 状态 ====================
  /** 排序状态（指标排序模式） */
  sorts: IRatioMetricSortState<T>[];
  /** 排序状态（通用排序模式） */
  sortState: ISortState | null;
  /** 分页状态 */
  pagingQuery: PagingQuery;
  /** 数据总数 */
  total: number;
  /** 分页大小选项 */
  pageSizeOptions: number[];

  // ==================== 回调 ====================
  /** 排序变化回调（指标排序模式） */
  handleSortsChange: (sorts: IRatioMetricSortState<T>[]) => void;
  /** 排序变化回调（通用排序模式） */
  handleSortChange: (sortState: ISortState) => void;
  /** 分页变化回调 */
  handlePagingChange: (pagingQuery: PagingQuery) => void;
  /** 重置所有状态，返回重置后的值 */
  resetState: () => IResetStateReturn<T>;
  /** 设置总数 */
  setTotal: (total: number) => void;

  // ==================== 数据处理 ====================
  /** 获取排序后的数据 */
  getSortedData: (data: T[]) => T[];
  /** 获取分页后的数据 */
  getPagedData: (data: T[]) => T[];
  /** 获取排序+分页后的数据 */
  getProcessedData: (data: T[]) => T[];

  // ==================== 分页信息与导航 ====================
  /** 获取分页信息 */
  getPaginationInfo: () => IPaginationInfo | null;
  /** 跳转到指定页 */
  goToPage: (pageIndex: number) => void;
  /** 下一页 */
  nextPage: () => void;
  /** 上一页 */
  prevPage: () => void;
  /** 是否有下一页 */
  hasNextPage: () => boolean;
  /** 是否有上一页 */
  hasPrevPage: () => boolean;
}

// ==================== 默认值 ====================

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// ==================== Hook 实现 ====================

/**
 * 本地表格状态管理 Hook
 * @param options 配置选项
 * @returns 表格状态和处理方法
 */
export default function useLocalTableState<T = any>(
  options: IUseLocalTableStateOptions<T> = {},
): IUseLocalTableStateReturn<T> {
  const {
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    defaultSorts = [],
    defaultSortState,
    sortMode = 'metric',
    customComparator,
  } = options;

  // ==================== 状态管理 ====================

  // 指标排序状态
  const [sorts, setSorts] = useState<IRatioMetricSortState<T>[]>(defaultSorts);

  // 通用排序状态
  const [sortState, setSortState] = useState<ISortState | null>(defaultSortState || null);

  // 分页状态
  const [pagingQuery, setPagingQuery] = useState<PagingQuery>({
    pageIndex: 1,
    pageSize: defaultPageSize,
  });

  // 总数
  const [total, setTotal] = useState<number>(0);

  // ==================== 回调函数 ====================

  /**
   * 排序变化（指标排序模式）
   */
  const handleSortsChange = (newSorts: IRatioMetricSortState<T>[]) => {
    setSorts(newSorts);
    // 排序变化时重置到第一页
    setPagingQuery((prev) => ({ ...prev, pageIndex: 1 }));
  };

  /**
   * 排序变化（通用排序模式）
   */
  const handleSortChange = (newSortState: ISortState) => {
    setSortState(newSortState);
    setPagingQuery((prev) => ({ ...prev, pageIndex: 1 }));
  };

  /**
   * 分页变化
   */
  const handlePagingChange = (newPagingQuery: PagingQuery) => {
    setPagingQuery(newPagingQuery);
  };

  /**
   * 重置状态
   * @returns 重置后的所有状态值
   */
  const resetState = (): IResetStateReturn<T> => {
    const newSorts = defaultSorts;
    const newSortState = defaultSortState || null;
    const newPagingQuery = { pageIndex: 1, pageSize: defaultPageSize };

    setSorts(newSorts);
    setSortState(newSortState);
    setPagingQuery(newPagingQuery);
    setTotal(0);

    return {
      sorts: newSorts,
      sortState: newSortState,
      pagingQuery: newPagingQuery,
    };
  };

  // ==================== 数据处理 ====================

  /**
   * 默认排序比较函数
   */
  const defaultComparator = (a: T, b: T, field: string, order: 'asc' | 'desc'): number => {
    const aVal = a?.[field as keyof T];
    const bVal = b?.[field as keyof T];
    const modifier = order === 'asc' ? 1 : -1;

    // 处理 null/undefined
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;

    // 数字比较
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * modifier;
    }

    // 字符串比较
    const aStr = String(aVal);
    const bStr = String(bVal);
    return aStr.localeCompare(bStr) * modifier;
  };

  /**
   * 获取排序后的数据
   */
  const getSortedData = (data: T[]): T[] => {
    if (isEmpty(data)) {
      return [];
    }

    // 指标排序模式
    if (sortMode === 'metric' && isNotEmpty(sorts)) {
      return multiRatioMetricSort([...data], sorts);
    }

    // 通用排序模式
    if (sortMode === 'generic' && sortState) {
      const comparator = customComparator || defaultComparator;
      return [...data].sort((a, b) => comparator(a, b, sortState.field, sortState.order));
    }

    return data;
  };

  /**
   * 获取分页后的数据
   */
  const getPagedData = (data: T[]): T[] => {
    if (isEmpty(data)) {
      return [];
    }
    const pageIndex = pagingQuery.pageIndex ?? 1;
    const pageSize = pagingQuery.pageSize ?? defaultPageSize;
    const startIndex = (pageIndex - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  };

  /**
   * 获取排序+分页后的数据
   */
  const getProcessedData = (data: T[]): T[] => {
    const sortedData = getSortedData(data);
    return getPagedData(sortedData);
  };

  // ==================== 分页信息与导航 ====================

  /**
   * 获取分页信息
   */
  const getPaginationInfo = (): IPaginationInfo | null => {
    if (total === 0) return null;

    const pageIndex = pagingQuery.pageIndex ?? 1;
    const pageSize = pagingQuery.pageSize ?? defaultPageSize;
    const totalPage = Math.ceil(total / pageSize);

    return {
      pageIndex,
      pageSize,
      total,
      totalPage,
      hasPrev: pageIndex > 1,
      hasNext: pageIndex < totalPage,
    };
  };

  /**
   * 获取总页数
   */
  const getTotalPage = (): number => {
    const pageSize = pagingQuery.pageSize ?? defaultPageSize;
    return Math.ceil(total / pageSize);
  };

  /**
   * 跳转到指定页
   */
  const goToPage = (pageIndex: number) => {
    const totalPage = getTotalPage();
    if (pageIndex < 1 || pageIndex > totalPage) return;
    setPagingQuery((prev) => ({ ...prev, pageIndex }));
  };

  /**
   * 下一页
   */
  const nextPage = () => {
    const totalPage = getTotalPage();
    const currentPageIndex = pagingQuery.pageIndex ?? 1;
    if (currentPageIndex < totalPage) {
      setPagingQuery((prev) => ({ ...prev, pageIndex: (prev.pageIndex ?? 1) + 1 }));
    }
  };

  /**
   * 上一页
   */
  const prevPage = () => {
    const currentPageIndex = pagingQuery.pageIndex ?? 1;
    if (currentPageIndex > 1) {
      setPagingQuery((prev) => ({ ...prev, pageIndex: (prev.pageIndex ?? 1) - 1 }));
    }
  };

  /**
   * 是否有下一页
   */
  const hasNextPage = (): boolean => {
    const currentPageIndex = pagingQuery.pageIndex ?? 1;
    return currentPageIndex < getTotalPage();
  };

  /**
   * 是否有上一页
   */
  const hasPrevPage = (): boolean => {
    const currentPageIndex = pagingQuery.pageIndex ?? 1;
    return currentPageIndex > 1;
  };

  // ==================== 返回值 ====================

  return {
    // 状态
    sorts,
    sortState,
    pagingQuery,
    total,
    pageSizeOptions,

    // 回调
    handleSortsChange,
    handleSortChange,
    handlePagingChange,
    resetState,
    setTotal,

    // 数据处理
    getSortedData,
    getPagedData,
    getProcessedData,

    // 分页信息与导航
    getPaginationInfo,
    goToPage,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
  };
}

// ==================== 工厂函数 ====================

/**
 * 创建默认指标排序状态
 * @param dataSource 数据源
 * @param metric 指标名称
 * @param type 排序方式
 * @param mode 排序模式：Number-数值排序，Ratio-环比排序
 */
export const createDefaultSorts = <T = any>(
  dataSource = DEFAULT_SENSORTOWER_DATA_SOURCE,
  metric = 'daily_avg_dau',
  type: SortType = SortType.Desc,
  mode: SortMode = SortMode.Number,
): IRatioMetricSortState<T>[] => [{ dataSource, metric, type, mode }];
